import * as React from 'react';
import clsx from 'clsx';
import styled, { css } from 'styled-components';
import { IBSelectState, IBSelectProps, ISelectOption } from './Select.types';
import {
  onSelectKeyDown,
  onSelectClick,
  onSelectOptionClick,
  prepareOptions
} from './Select.logic';
import { SvgImage } from '../SvgImage';
import { getAssetPath } from '../../util/images';
import {
  StyledDropdown,
  StyledDropdownItem,
  StyledDropdownPlaceholder,
  StyledDropdownSelection
} from './DropdownComponents';
import { InputBase } from '../Input/Input';

const selectInput = (disabled?: boolean, inputError?: boolean) => css`
  outline: none;
  margin: unset;
  padding-right: 30px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border: 0.75px solid #a5a5a5;
  background-color: ${({ theme }) =>
    disabled ? theme.colors.d4 : theme.colors.background3} !important;
  color: ${({ theme }) =>
    disabled ? theme.colors.d3gray : theme.colors.font1};

  .dropdown-item {
    background-color: ${({ theme }) => theme.colors.background3};
    color: ${({ theme }) => theme.colors.font1};
  }
  ${({ theme }) =>
    disabled &&
    `
      &:focus,
      &:hover {
        outline: none;
        border-color:
        ${inputError ? theme.colors.alert : theme.colors.d3gray} !important;
        cursor: default;
      }
      `};
`;

const selectArrow = () => css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 10px;

  svg path {
    fill: ${({ theme }) => theme.colors.font1};
  }
`;

export const FilterSelectList = styled.div`
  width: 100%;
  position: relative;
`;

export const SelectInputWrapper = styled.div<{
  disabled?: boolean;
  inputError?: boolean;
}>`
  ${({ disabled, inputError }) => selectInput(disabled, inputError)};
  width: 100%;
  margin: unset;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  height: 36px;
  padding-left: 8px;
  select {
    border: 0;
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
    -moz-appearance: none; /* Firefox */
    -webkit-appearance: none; /* Safari and Chrome */
    appearance: none;
  }
`;

export const SelectInput = styled(InputBase)<{
  disabled?: boolean;
  inputError?: boolean;
}>`
  ${({ disabled, inputError }) => selectInput(disabled, inputError)};
  width: 100%;
  margin: unset;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  height: 36px;
  padding-left: 8px;
`;

export const SelectArrowSvg = styled(SvgImage)`
  ${selectArrow}
`;

export const SelectArrowImg = styled.img`
  ${selectArrow}
`;

const SelectValue: React.FC<{
  value: ISelectOption;
  placeholder?: string;
  useGroups: boolean;
}> = (props: {
  value: ISelectOption;
  placeholder?: string;
  useGroups: boolean;
  testId?: string;
}) => {
  const { value, placeholder, useGroups, testId } = props;
  if (value && value.value) {
    return (
      <StyledDropdownSelection
        className="cut-text w-100p"
        data-testid={testId || 'select-value'}
      >
        {useGroups ? (
          <>{value.groupLabel ? `${value.groupLabel} - ` : ''}</>
        ) : null}
        {value.label}
      </StyledDropdownSelection>
    );
  }
  return <StyledDropdownPlaceholder>{placeholder}</StyledDropdownPlaceholder>;
};

const Select: React.FC<IBSelectProps> = (props: IBSelectProps) => {
  const {
    className,
    options,
    onChange,
    value,
    addEmptyValue,
    placeholder,
    error,
    errorMessage,
    disabled,
    styleType,
    useGroups,
    testId,
    maxDropdownHeight
  } = props;
  const [state, setState] = React.useState<IBSelectState>({ show: false });
  const handleOnSelectKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) =>
    onSelectKeyDown(e, disabled, state, setState);
  const handleOnSelectClick = () => onSelectClick(disabled, state, setState);
  const handleOnSelectOptionClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    option: ISelectOption
  ) => onSelectOptionClick(e, option, onChange, setState);

  React.useEffect(() => {
    const clickHandler = () => {
      setState({ show: false });
    };

    if (state.show) {
      document.addEventListener('click', clickHandler);
    }

    return () => {
      document.removeEventListener('click', clickHandler);
    };
  }, [state.show]);

  return (
    <div className={className}>
      <FilterSelectList>
        <SelectInputWrapper
          inputError={error || (errorMessage && errorMessage.length > 0)}
          disabled={disabled}
          id="listbox1"
          role="listbox"
          aria-activedescendant="listbox1-1"
          tabIndex={0}
          className={clsx({
            'input-base': true,
            input: !styleType || styleType === 'standard',
            'input-inline': styleType && styleType === 'inline'
          })}
          onClick={handleOnSelectClick}
          onKeyDown={handleOnSelectKeyDown}
          data-testid={testId || 'select-input'}
        >
          <SelectArrowSvg
            className="select--select-arrow"
            inAssetsPath={getAssetPath('icons/select-arrow.svg')}
          />
          <SelectValue
            value={value}
            useGroups={useGroups}
            placeholder={placeholder}
          />
          {options && options.length ? (
            <StyledDropdown
              inline={styleType === 'inline'}
              hidden={!state.show}
              className={clsx({
                'w-100': true
              })}
              maxDropdownHeight={maxDropdownHeight}
            >
              {addEmptyValue ? (
                <StyledDropdownItem
                  id="listbox1-1"
                  role="option"
                  aria-selected="true"
                  tabIndex={0}
                  type="button"
                  className="dropdown-item small-text"
                  onClick={e => {
                    handleOnSelectOptionClick(e, { value: '', label: '' });
                  }}
                >
                  {' '}
                </StyledDropdownItem>
              ) : null}
              {prepareOptions(options, useGroups).map((option, index) => {
                return (
                  <StyledDropdownItem
                    id={`listbox1 - ${index + 2} `}
                    role="option"
                    aria-selected="false"
                    key={option.value}
                    type="button"
                    className="dropdown-item small-text-medium"
                    onClick={e => {
                      handleOnSelectOptionClick(e, option);
                    }}
                  >
                    {useGroups ? (
                      <div className="inline-flex-container w-100p">
                        <div className="w-20p">
                          {option.groupLabelVisible ?? option.group}
                        </div>
                        <div className="w-80p cut-text">{option.label}</div>
                      </div>
                    ) : (
                      option.label
                    )}
                  </StyledDropdownItem>
                );
              })}
            </StyledDropdown>
          ) : null}
        </SelectInputWrapper>
      </FilterSelectList>
    </div>
  );
};

export default Select;
